import { useCallback, useEffect, useRef, useState } from 'react';
import useHttp from 'hooks/useHttp';
import { PENDING_POSITION_REQUEST_COUNT_URL } from 'api/positionChangeRequest';
import { getEmployeeId, isAdmin } from 'utils/auth';

export default function usePositionRequestCount(enabled, employeeId) {
    const { sendRequest } = useHttp();
    const send = useRef(sendRequest);
    send.current = sendRequest;
    const [state, setState] = useState({ employeeId, count: null, loading: false, error: '' });
    const generation = useRef(0);
    const controller = useRef(null);
    const refreshPendingCount = useCallback(async () => {
        if (!enabled || !employeeId || getEmployeeId() !== employeeId || !isAdmin()) {
            return;
        }
        const sequence = ++generation.current;
        controller.current?.abort();
        controller.current = new AbortController();
        setState((previous) => ({
            employeeId, count: previous.employeeId === employeeId ? previous.count : null,
            loading: true, error: '',
        }));
        const result = await send.current({
            url: PENDING_POSITION_REQUEST_COUNT_URL, signal: controller.current.signal,
        });
        if (sequence !== generation.current || getEmployeeId() !== employeeId || !isAdmin()) {
            return;
        }
        if (result.ok && Number.isInteger(result.data?.count) && result.data.count >= 0) {
            setState({ employeeId, count: result.data.count, loading: false, error: '' });
        } else {
            setState((previous) => ({
                ...previous, loading: false,
                error: 'Pending position request count is unavailable. Any displayed count is the last known count.',
            }));
        }
    }, [enabled, employeeId]);

    useEffect(() => {
        setState({ employeeId, count: null, loading: false, error: '' });
        refreshPendingCount();
        window.addEventListener('focus', refreshPendingCount);
        window.addEventListener('online', refreshPendingCount);
        return () => {
            generation.current++;
            controller.current?.abort();
            window.removeEventListener('focus', refreshPendingCount);
            window.removeEventListener('online', refreshPendingCount);
        };
    }, [employeeId, refreshPendingCount]);

    const current = enabled && state.employeeId === employeeId;
    return {
        pendingPositionCount: current ? state.count : null,
        pendingPositionCountLoading: current && state.loading,
        pendingPositionCountError: current ? state.error : '',
        refreshPendingCount,
    };
}
